import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../api/axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaUsers, FaCheck, FaTimes, FaTrash } from "react-icons/fa";
import { useTranslation } from "../context/LanguageContext";
import DeleteConfirmModal from "../components/DeleteConfirmModal";
import "../styles/common.css";
import "./ModifierTroupeau.css";

export default function ModifierTroupeau() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    nom: "",
    berger: null,
  });
  const [initialData, setInitialData] = useState(null);
  const [bergers, setBergers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fieldErrors, setFieldErrors] = useState({
    nom: "",
    berger: "",
  });
  const [touchedFields, setTouchedFields] = useState({
    nom: false,
    berger: false,
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const fetchTroupeau = async () => {
      try {
        const response = await api.get(`/api/troupeaux/${id}/`);
        setFormData(response.data);
        setInitialData(JSON.parse(JSON.stringify(response.data)));
        setLoading(false);
      } catch (error) {
        console.error("Erreur lors du chargement du troupeau:", error);
        setLoading(false);
      }
    };
    fetchTroupeau();
  }, [id]);

  useEffect(() => {
    const fetchBergers = async () => {
      try {
        const bergersRes = await api.get("/api/bergers/");
        
        // Gérer la réponse de l'API (peut être un tableau ou un objet avec pagination)
        const bergers = Array.isArray(bergersRes.data) ? bergersRes.data : (bergersRes.data.results || []);
        
        setBergers(bergers);
      } catch (error) {
        console.error("Erreur lors du chargement des données:", error);
      }
    };
    fetchBergers();
  }, []);

  // Fonction de validation des champs obligatoires
  const validateRequiredField = (name, value) => {
    if (!value || (typeof value === 'string' && value.trim() === "") || value === null) {
      return "Ce champ est obligatoire";
    }
    return "";
  };

  // Validation d'un champ spécifique
  const validateField = (name, value) => {
    if (name === "nom" || name === "berger") {
      return validateRequiredField(name, value);
    }
    return "";
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    // Validation en temps réel si le champ a été touché
    if (touchedFields[name] || fieldErrors[name]) {
      const error = validateField(name, value);
      setFieldErrors(prev => ({
        ...prev,
        [name]: error
      }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    
    // Marquer le champ comme touché
    if (touchedFields.hasOwnProperty(name)) {
      setTouchedFields(prev => ({
        ...prev,
        [name]: true
      }));
    }
    
    // Valider le champ (convertir la valeur pour berger)
    const fieldValue = name === "berger" ? (value ? Number(value) : null) : value;
    const error = validateField(name, fieldValue);
    setFieldErrors(prev => ({
      ...prev,
      [name]: error
    }));
  };

  const hasChanges = () => {
    if (!initialData) return false;
    // Normaliser les valeurs pour la comparaison (null/undefined -> null)
    const normalize = (val) => (val === null || val === undefined) ? null : val;
    return formData.nom !== initialData.nom || 
           normalize(formData.berger) !== normalize(initialData.berger);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Marquer tous les champs obligatoires comme touchés
    const newTouchedFields = {
      nom: true,
      berger: true,
    };
    setTouchedFields(newTouchedFields);
    
    // Valider tous les champs obligatoires
    const newFieldErrors = {
      nom: validateField("nom", formData.nom),
      berger: validateField("berger", formData.berger),
    };
    setFieldErrors(newFieldErrors);
    
    // Vérifier s'il y a des erreurs
    const hasErrors = Object.values(newFieldErrors).some(error => error !== "");
    
    if (hasErrors) {
      return; // Ne pas soumettre si il y a des erreurs
    }
    
    try {
      // Préparer les données pour l'API
      const dataToSubmit = {
        nom: formData.nom,
        berger: formData.berger,
      };
      await api.put(`/api/troupeaux/${id}/`, dataToSubmit);
      toast.success("Troupeau modifié avec succès !");
      // Navigate after a short delay to allow the toast to be seen
      setTimeout(() => {
        navigate("/troupeaux");
      }, 500);
    } catch (error) {
      console.error("Erreur lors de la mise à jour:", error);
      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.message ||
                          Object.values(error.response?.data || {}).flat().join(", ") ||
                          "Une erreur s'est produite lors de la mise à jour.";
      toast.error(errorMessage);
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      await api.delete(`/api/troupeaux/${id}/`);
      setShowDeleteModal(false);
      toast.success("Troupeau supprimé avec succès !");
      setTimeout(() => {
        navigate("/troupeaux");
      }, 500);
    } catch (error) {
      console.error("Erreur lors de la suppression:", error);
      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.message ||
                          Object.values(error.response?.data || {}).flat().join(", ") ||
                          "Une erreur s'est produite lors de la suppression.";
      toast.error(errorMessage);
      setShowDeleteModal(false);
    } finally {
      setIsDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="modifier-troupeau-page">
        <div className="loading-container">
          <p className="loading-text">Chargement du troupeau...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="modifier-troupeau-page">
      <div className="title-badge">
        <FaUsers className="page-title-icon" aria-hidden="true" />
        <span>MODIFIER LE TROUPEAU {formData.nom || id}</span>
      </div>

      <form onSubmit={handleSubmit} className="troupeau-form">
        {/* Bannière d'information */}
        <div className="form-info-banner">
          <span className="info-icon">ℹ️</span>
          <div className="info-content">
            <strong>Champs obligatoires :</strong>
            <span className="required-indicator">*</span> Les champs marqués d'un astérisque rouge sont obligatoires.
          </div>
        </div>

        <div className="form-section-container">
          <div className="form">
            <label htmlFor="nom">
              Nom <span className="required-indicator">*</span>
            </label>
            <input
              type="text"
              id="nom"
              name="nom"
              value={formData.nom}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder={t.herdNameLabel}
              className={fieldErrors.nom && touchedFields.nom ? "input-error" : ""}
              required
            />
            {fieldErrors.nom && touchedFields.nom && (
              <span className="field-error-message">{fieldErrors.nom}</span>
            )}
          </div>

          <div className="form">
            <label htmlFor="berger">
              {t.shepherdField} <span className="required-indicator">*</span>
            </label>
            <select
              id="berger"
              name="berger"
              value={formData.berger || ""}
              onChange={(e) => {
                const value = e.target.value;
                setFormData({ ...formData, berger: value ? Number(value) : null });
                // Valider après changement
                if (touchedFields.berger || fieldErrors.berger) {
                  const error = validateField("berger", value ? Number(value) : null);
                  setFieldErrors(prev => ({
                    ...prev,
                    berger: error
                  }));
                }
              }}
              onBlur={handleBlur}
              className={fieldErrors.berger && touchedFields.berger ? "input-error" : ""}
              required
            >
              <option value="">{t.selectShepherd}</option>
              {bergers.map((berger) => {
                const displayName = berger.nom_complet || `${berger.prenom || ""} ${berger.nom || ""}`.trim() || t.noName;
                return (
                  <option key={berger.id} value={berger.id}>
                    {displayName}
                  </option>
                );
              })}
            </select>
            {fieldErrors.berger && touchedFields.berger && (
              <span className="field-error-message">{fieldErrors.berger}</span>
            )}
          </div>
        </div>

        {/* Boutons d'action */}
        <div className="actions">
          <button
            type="submit"
            className="save-but"
            disabled={!hasChanges()}
          >
            <FaCheck />
            <span>Enregistrer</span>
          </button>
          <button
            type="button"
            className="delete-but"
            onClick={handleDeleteClick}
          >
            <FaTrash />
            <span>Supprimer</span>
          </button>
          <button
            type="button"
            className="cancel-but"
            onClick={() => navigate("/troupeaux")}
          >
            <FaTimes />
            <span>Annuler</span>
          </button>
        </div>
      </form>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />

      {/* Modal de suppression */}
      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        itemName={formData.nom}
        itemType="troupeau"
        consequences={[
          "La suppression définitive de toutes les données du troupeau",
          "La suppression de tous les animaux associés au troupeau",
        ]}
        requireConfirmation={false}
        isLoading={isDeleting}
      />
    </div>
  );
}
